import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Accordion, AccordionTab } from "primereact/accordion";
import { ProgressSpinner } from "primereact/progressspinner";
import "primeicons/primeicons.css";
import { ApiEndpoints, Constants } from "../../constants/appConstants";
import { useLocation, useSearchParams } from "react-router";
import httpService from "../../services/httpService";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import "./ProviderProfileComponent.scss";
import { Tooltip } from "primereact/tooltip";
import { Badge } from "primereact/badge";
import {
  alphaNumericSort,
  PerformanceArea,
  ReviewRquest,
  Standard,
  TierQuestions,
  Status,
  ReviewStatusCode,
  Comment,
  ReviewType,
  TierCode,
  ProviderType,
  ReviewState,
  Measure,
} from "./models";
import StandardsDetailsComponent from "./StandardsDetailsComponent";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { useAuthContext } from "../../Context/AuthContext";
import CommentsSidebar from "./CommentsSidebar";
import { useAppContext } from "../../Context";
import MultipartFileUploadComponent from "../FileUpload/MultpartFileUploadComponent";
import CommonDialogWindowComponent from "../CommonDialogWindowComponent/CommonDialogWindowComponent";
import OutcomeStatusTableComponent from "./OutcomeStatusTableComponent";
import ReviewStageTrackerComponent from "./ReviewStageTrackerComponent";
import type { StageReviewer, ReviewActivityLog } from "./ReviewStageTrackerComponent";
import SmeReviewStatsComponent from "./SmeReviewStatsComponent";
import SendEmailDialogComponent from "./SendEmailDialogComponent";

const RHProviderProfileComponent: React.FC<{ reviewYear?: number }> = ({ reviewYear: propReviewYear }) => {
  const { appUser, isAdministrator, isFinalReviewer, isSmeReviewer, isInitialReviewer } =
    useAuthContext();
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const location = useLocation();
  const [provider, setSelectedProvider] = useState<any>({});
  const [providerContactDetails, setProviderContactDetails] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [fileUploaded, setFileUploaded] = useState<boolean>(false);
  const [status, setStatus] = useState<Status[]>([]);
  const [performanceAreas, setPerformanceAreas] = useState<PerformanceArea[]>([]);
  const [visible, setVisible] = useState(false);
  const [reviweResponses, setReviewResponses] = useState<ReviewRquest[]>([]);
  const [calculatedTier, setCalculatedTier] = useState<string>();
  const [isCalculate, setIsCalculate] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<Status | null | undefined>(null);
  const [isActiveReview, setIsActiveReview] = useState(false);
  const [isActiveDisputeReview, setIsActiveDisputeReview] = useState(false);
  const [appliedTierId, setAppliedTierId] = useState<number>(0);
  const [allStatues, setAllStatues] = useState<Status[]>([]);
  const [isStatusLoaded, setIsStatusLoaded] = useState<boolean>(false);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [commentProviderMPI, setCommentProviderMPI] = useState(
    location?.state?.provider?.mpi
  );
  const [commentMeasureNumber, setCommentMeasureNumber] = useState("");
  const [commentMeasureId, setCommentMeasureId] = useState(0);
  const [commentLevel, setCommentLevel] = useState("");
  const [providerId, setProviderId] = useState(0);
  const [allCommentData, setAllCommentData] = useState<Comment[]>([]);
  const [allProviderLevelCommentData, setAllProviderLevelCommentData] = useState<Comment[]>([]);
  const { reviewYear: contextReviewYear } = useAppContext();
  const reviewYear = propReviewYear ?? contextReviewYear;
  const [searchParams] = useSearchParams();
  const [providerAdditionalInfo, setProviderAdditionalInfo] = useState<any>({});
  const [statusesByRole, setStatusesByRole] = useState<any[]>([]);
  const [measureEvaluationStatuses, setMeasureEvaluationStatuses] = useState<any[]>([]);
  const [reviewers, setReviewers] = useState<any[]>([]);
  const [canReview, setCanReview] = useState<boolean>(false);
  const [canStartReview, setCanStartReview] = useState<boolean>(false);
  const dialogRef = useRef<any>(null);
  const toast = useRef<Toast>(null);

  const [changingStatus, setChangingStatus] = useState<boolean>(false);
  const [updatingStatus, setUpdatingStatus] = useState<boolean>(false);
  const [outcomeStatusTable, setOutcomeStatusTable] = useState<any | null>(null);
  const [outcomeStatusLoading, setOutcomeStatusLoading] = useState(false);
  const [outcomeStatusError, setOutcomeStatusError] = useState<string | null>(null);

  const [graduateMeasures, setGraduateMeasures] = useState<any[]>([]);
  const [emailDialogVisible, setEmailDialogVisible] = useState<boolean>(false);

  //const openEmailDialog = useCallback(() => {
  //  setEmailDialogVisible(true);
  //}, []);

  const handleEmailDialogHide = useCallback(() => {
    setEmailDialogVisible(false);
  }, []);

  const handleSendEmail = useCallback((_payload: {
    subject: string;
    message: string;
    attachments: File[];
    toEmail?: string;
  }) => {
    // Sending is handled by SendEmailDialogComponent.
  }, []);

  const isReviewCycleCalledRef = useRef(false);
  const isDisputeCycleCalledRef = useRef(false);
  const allStatusCalledRef = useRef(false);
  const measureEvaluationStatusesCalledRef = useRef(false);
  const providerInitializedRef = useRef(false);
  const statusesByUserCalledRef = useRef(false);

  const handleCommentsSidebarHide = useCallback(() => {
    setSidebarVisible(false);
    setCommentMeasureId(0);
    setCommentMeasureNumber("");
    setCommentLevel("");
  }, []);

  const filterMeasures = useCallback((predicate: (measure: any) => boolean): any[] => {
    if (!performanceAreas || !Array.isArray(performanceAreas)) {
      return [];
    }
    return performanceAreas
      .filter((area) => area && area.standards && Array.isArray(area.standards))
      .flatMap((area) =>
        area.standards
          .filter((standard) => standard && standard.measures && Array.isArray(standard.measures))
          .flatMap((standard) => standard.measures.filter((measure) => measure && predicate(measure)))
      );
  }, [performanceAreas]);

  const countTotalMeasures = useMemo((): number => {
    if (!performanceAreas || !Array.isArray(performanceAreas)) {
      return 0;
    }
    return performanceAreas.reduce((acc, area) => {
      if (!area?.standards || !Array.isArray(area.standards)) {
        return acc;
      }
      return acc + area.standards.reduce((innerAcc, standard) => {
        return innerAcc + (standard?.measures?.length || 0);
      }, 0);
    }, 0);
  }, [performanceAreas]);

  const isUnreviewed = useCallback((reviewResponse: string | null | undefined): boolean => {
    return (
      !reviewResponse ||
      reviewResponse === null ||
      reviewResponse === undefined ||
      reviewResponse?.trim().toLowerCase() === "null" ||
      reviewResponse?.trim().toLowerCase() === "nr"
    );
  }, []);

  const updateStatusDropDownGeneric = useCallback((isDispute: boolean): Status[] => {
    if (!provider?.reviewStatusCode || !allStatues || allStatues.length === 0) {
      return [];
    }

    const statusCode = provider.reviewStatusCode.toLowerCase();
    const codes = isDispute ? {
      notStarted: ReviewStatusCode.RsResolutionNotStarted.toLowerCase(),
      initialReview: ReviewStatusCode.RsResolutionInitialReview.toLowerCase(),
      smeReview: ReviewStatusCode.RsResolutionSMEReview.toLowerCase(),
      finalReview: ReviewStatusCode.RsResolutionFinalReview.toLowerCase(),
      completed: ReviewStatusCode.RsResolutionReviewCompleted.toLowerCase()
    } : {
      notStarted: ReviewStatusCode.RsNotStarted.toLowerCase(),
      initialReview: ReviewStatusCode.RsInitialReview.toLowerCase(),
      smeReview: ReviewStatusCode.RsSMEReview.toLowerCase(),
      finalReview: ReviewStatusCode.RsFinalReview.toLowerCase(),
      completed: ReviewStatusCode.RsReviewCompleted.toLowerCase()
    };

    let requiredStatuses: Status[];

    if (statusCode === codes.notStarted || statusCode === ReviewStatusCode.NotStarted?.toLowerCase()) {
      requiredStatuses = allStatues.filter((s) =>
        s.statusCode?.toLowerCase() === codes.notStarted ||
        s.statusCode?.toLowerCase() === codes.initialReview
      );
    } else if (statusCode === codes.initialReview) {
      requiredStatuses = allStatues.filter((s) =>
        s.statusCode?.toLowerCase() === codes.initialReview ||
        s.statusCode?.toLowerCase() === codes.finalReview
      );
    } else if (statusCode === codes.smeReview) {
      requiredStatuses = allStatues.filter((s) =>
        s.statusCode?.toLowerCase() === codes.smeReview ||
        s.statusCode?.toLowerCase() === codes.finalReview
      );
    } else if (statusCode === codes.finalReview) {
      requiredStatuses = allStatues.filter((s) =>
        s.statusCode?.toLowerCase() === codes.finalReview ||
        s.statusCode?.toLowerCase() === codes.completed
      );
    } else {
      requiredStatuses = allStatues.filter((s) =>
        s.statusCode?.toLowerCase() === codes.completed
      );
    }

    return requiredStatuses;
  }, [provider?.reviewStatusCode, allStatues]);

  const safeJsonParse = useCallback((jsonString: string | null | undefined, fallback: any = null): any => {
    if (!jsonString) return fallback;
    try {
      return JSON.parse(jsonString);
    } catch (e: any) {
      console.error("[RHProviderProfileComponent] JSON parse failed.", {
        message: e?.message,
        stack: e?.stack
      });
      return fallback;
    }
  }, []);

  const getSelectedStatus = useCallback((statusName: string) => {
    if (allStatues && allStatues.length > 0) {
      return (
        allStatues.find(
          (status) =>
            status.statusName?.toLowerCase() === statusName?.toLowerCase()
        ) || null
      );
    }
    return undefined;
  }, [allStatues]);

  const updateStatusDropDown = useCallback(() => {
    return updateStatusDropDownGeneric(false);
  }, [updateStatusDropDownGeneric]);

  const updateStatusDropDownForDispute = useCallback(() => {
    return updateStatusDropDownGeneric(true);
  }, [updateStatusDropDownGeneric]);

  const hasAnySMEReviewForMeasure = useCallback((): boolean => {
    return filterMeasures((m) => m?.reviewResponse?.toLowerCase() === "sme").length > 0;
  }, [filterMeasures]);

  const hasUnReviewedMeasures = useCallback((): boolean => {
    return filterMeasures((m) => isUnreviewed(m?.reviewResponse)).length > 0;
  }, [filterMeasures, isUnreviewed]);

  const getReviewersDetails = useCallback((mpi: string) => {
    if (!mpi) {
      return;
    }

    httpService
      .get(`${ApiEndpoints.GetReviewersDetailsByMpi}/${reviewYear}/${mpi}`)
      .then((response: any) => {
        if (response.status === 200) {
          setReviewers(response.data);
        }
      })
      .catch((error: any) => {
        console.error("[RHProviderProfileComponent] Failed to load reviewer details.", {
          mpi,
          message: error?.message,
          responseStatus: error?.response?.status,
          responseData: error?.response?.data,
          innerExceptionMessage: error?.cause?.message,
          stack: error?.stack
        });
      });
  }, [reviewYear]);

  const fetchActivityLogs = useCallback(
    async (reviewer: StageReviewer): Promise<ReviewActivityLog[]> => {
      if (!provider?.mpi || !reviewer?.reviewStatusCode || !reviewYear) {
        return [];
      }

      try {
        const response = await httpService.get(
          `${ApiEndpoints.GetReviewActivityLogs}/${reviewYear}/${provider.mpi}/${reviewer.reviewStatusCode}`
        );

        return response?.status === 200 && Array.isArray(response?.data)
          ? (response.data as ReviewActivityLog[])
          : [];
      } catch (error: any) {
        console.error("[RHProviderProfileComponent] Failed to load review activity logs.", {
          reviewYear,
          mpi: provider?.mpi,
          reviewStatusCode: reviewer?.reviewStatusCode,
          message: error?.message,
          responseStatus: error?.response?.status,
          responseData: error?.response?.data,
          stack: error?.stack
        });
        return [];
      }
    },
    [reviewYear, provider?.mpi]
  );

  //For handling dupplicated api called for provider and statusId
  const updateStatusInFlightRef = useRef<Set<string>>(new Set());
  const lastUpdateStatusSentRef = useRef<string | null>(null);

  // updateStatus function - must be before changeStatusToFinalReview
  const updateStatus = useCallback((e: Status) => {
    if (!provider || !provider.providerId || !provider.mpi || !e || !e.statusId) {
      return;
    }
    const dedupeKey = `${provider.providerId}:${e.statusId}`;

    if (provider.reviewStatusId === e.statusId) {
      return;
    }

    if (updateStatusInFlightRef.current.has(dedupeKey)) {
      return;
    }

    if (lastUpdateStatusSentRef.current === dedupeKey) {
      return;
    }
    updateStatusInFlightRef.current.add(dedupeKey);
    setUpdatingStatus(true); // Start loading
    httpService
      .post(ApiEndpoints.SetReviewStatus, {
        ProviderId: provider.providerId,
        StatusId: e.statusId,
        reviewerName: appUser?.name ?? appUser?.email,
        ReviewYear: reviewYear,
        MpiId: provider.mpi,
        providerType: ProviderType.RS,
      })
      .then((response: any) => {
        if (response?.status === 200) {
          lastUpdateStatusSentRef.current = dedupeKey;
          setSelectedStatus(e);
          setChangingStatus(false);

          if (provider && provider.mpi) {
            getReviewersDetails(provider.mpi);
          }

          const currentReviewType =
            e.statusCode.toUpperCase() === ReviewStatusCode.InPrimaryReview
              ? ReviewType.PrimaryReview
              : e.statusCode.toUpperCase() === ReviewStatusCode.InPeerReview
              ? ReviewType.SecondaryReview
              : provider.reviewType;

          setSelectedProvider({
            ...provider,
            reviewStatusCode: e.statusCode,
            reviewStatus: e.statusName,
            reviewStatusId: e.statusId,
            reviewType: currentReviewType,
          });

          if (Constants.IS_REALTIME_REVIEW_STATUS_SYNC_ENABLED) {
          }

          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: `Status updated to ${e.statusName} successfully`,
            life: 3000
          });
        }
      })
      .catch((error: any) => {
        console.error("[RHProviderProfileComponent] Failed to update status.", {
          statusId: e?.statusId,
          statusName: e?.statusName,
          providerId: provider?.providerId,
          mpi: provider?.mpi,
          message: error?.message,
          responseStatus: error?.response?.status,
          responseData: error?.response?.data,
          innerExceptionMessage: error?.cause?.message,
          stack: error?.stack
        });

        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to update status. Please try again.",
          life: 3000
        });
      })
      .finally(() => {
        updateStatusInFlightRef.current.delete(dedupeKey);
        setUpdatingStatus(false); // Stop loading
      });
  }, [provider, appUser, reviewYear, getReviewersDetails]);

  const updatePerformanceAreasForComments = useCallback((
    measureId: number,
    isCommentsLoaded: boolean = false
  ) => {
    if (!performanceAreas || performanceAreas.length === 0) {
      return;
    }

    if (performanceAreas && performanceAreas.length > 0 && measureId > 0) {
      setPerformanceAreas((prevPerformanceAreas) =>
        prevPerformanceAreas.map((area) => ({
          ...area,
          standards: area.standards.map((standard) => ({
            ...standard,
            measures: standard.measures.map((measure) => {
              if (measure.measureId === measureId) {
                return { ...measure, isCommentsAvailable: true };
              }
              return measure;
            }),
          })),
        }))
      );
    } else if (isCommentsLoaded) {
      setPerformanceAreas((prevPerformanceAreas) =>
        prevPerformanceAreas.map((area) => ({
          ...area,
          standards: area.standards.map((standard) => ({
            ...standard,
            measures: standard.measures.map((measure) => {
              if (
                allCommentData &&
                Array.isArray(allCommentData) &&
                allCommentData.some((x) => x.measureId == measure.measureId)
              ) {
                return { ...measure, isCommentsAvailable: true };
              }
              return measure;
            }),
          })),
        }))
      );
    }
  }, [performanceAreas, allCommentData]);

  const changeStatusToFinalReview = useCallback(() => {
    if (
      allStatues &&
      allStatues.length > 0 &&
      performanceAreas &&
      performanceAreas.length > 0
    ) {
      const requiredFinalStatus = isActiveDisputeReview
        ? ReviewStatusCode.RsResolutionFinalReview.toLowerCase()
        : ReviewStatusCode.RsFinalReview.toLowerCase();

      const finalReviewStatus = allStatues.find(
        (s) => s.statusCode?.toLowerCase() === requiredFinalStatus
      );

      if (!hasUnReviewedMeasures() && !hasAnySMEReviewForMeasure()) {
        if ((isSmeReviewer() || isAdministrator()) && finalReviewStatus && changingStatus) {
          if (
            provider && (isActiveDisputeReview &&
              provider.reviewStatusCode?.toLowerCase() !== ReviewStatusCode.RsResolutionFinalReview.toLowerCase()) ||
            provider.reviewStatusCode?.toLowerCase() !== ReviewStatusCode.RsFinalReview.toLowerCase()
          ) {
            if (
              provider &&
              (isActiveDisputeReview &&
                provider.reviewStatusCode?.toLowerCase() !== ReviewStatusCode.RsResolutionFinalReview.toLowerCase()) ||
              provider.reviewStatusCode?.toLowerCase() !== ReviewStatusCode.RsFinalReview.toLowerCase() &&
              changingStatus
            ) {
              setChangingStatus(false);
              updateStatus(finalReviewStatus);
            }
            setChangingStatus(false);
          }
        }
      }
    }
  }, [allStatues, performanceAreas, isActiveDisputeReview, provider, changingStatus, hasUnReviewedMeasures, hasAnySMEReviewForMeasure, isSmeReviewer, isAdministrator, updateStatus]);

  const handleRequiredStatusOption = useCallback(() => {
    let requiredStatuses = isActiveDisputeReview ? updateStatusDropDownForDispute() : updateStatusDropDown();
    const smeStatusCode = isActiveDisputeReview
      ? ReviewStatusCode.RsResolutionSMEReview.toLowerCase()
      : ReviewStatusCode.RsSMEReview.toLowerCase();
    const hasSMEReview = hasAnySMEReviewForMeasure();

    if (hasSMEReview) {
      if (
        !requiredStatuses.some(
          (s) => s.statusCode?.toLowerCase() === smeStatusCode
        )
      ) {
        const smeStatus = allStatues.find(
          (s) => s.statusCode?.toLowerCase() === smeStatusCode
        );
        if (smeStatus) requiredStatuses = [...requiredStatuses, smeStatus];
      }
    } else if (hasUnReviewedMeasures()) {
      requiredStatuses = requiredStatuses.filter(
        (s) =>
          s.statusCode?.toLowerCase() !==
          ReviewStatusCode.RsReviewCompleted.toLowerCase()
      );
    } else {
      requiredStatuses = requiredStatuses.filter(
        (s) => s.statusCode?.toLowerCase() !== smeStatusCode
      );
    }

    setStatus(requiredStatuses);
  }, [isActiveDisputeReview, updateStatusDropDownForDispute, updateStatusDropDown, hasAnySMEReviewForMeasure, hasUnReviewedMeasures, allStatues]);

  const getgraduateMeasures = () => {
    if (!appliedTierId || appliedTierId <= 0) {
      return;
    }

    httpService
      .get(`${ApiEndpoints.GetGraduatedMeasuresInfo}/${appliedTierId}`)
      .then((response: any) => {
        if (response?.status === 200 && response?.data) {
          setGraduateMeasures(response.data);
        }
      })
      .catch((error: any) => {
        console.error("[RHProviderProfileComponent] Failed to load graduate measures.", {
          appliedTierId,
          message: error?.message,
          responseStatus: error?.response?.status,
          responseData: error?.response?.data,
          innerExceptionMessage: error?.cause?.message,
          stack: error?.stack
        });
      });
  };

  const getMeasureEvaluationStatuses = () => {
    if (measureEvaluationStatusesCalledRef.current) {
      return;
    }

    measureEvaluationStatusesCalledRef.current = true;

    httpService
      .get(`${ApiEndpoints.GetMeasureEvaluationStatuses}?providerType=${ProviderType.RS}`)
      .then((response: any) => {
        if (response.status === 200) {
          setMeasureEvaluationStatuses(response.data);
        }
      })
      .catch((error: any) => {
        measureEvaluationStatusesCalledRef.current = false;
        console.error("[RHProviderProfileComponent] Failed to load measure evaluation statuses.", {
          message: error?.message,
          responseStatus: error?.response?.status,
          responseData: error?.response?.data,
          innerExceptionMessage: error?.cause?.message,
          stack: error?.stack
        });
      });
  };

useEffect(()=>{
  if(appliedTierId && appliedTierId > 0){
    getgraduateMeasures();
  }

},[appliedTierId])

  useEffect(() => {
    if (!canReview || !reviewers || reviewers.length === 0 || !provider || !provider.reviewStatusCode) {
      return;
    }
    
    let canStart = false;
    if (
        ![
          ReviewStatusCode.RsNotStarted?.toLowerCase(),
          ReviewStatusCode.NotStarted?.toLowerCase(),
          ReviewStatusCode.RsResolutionNotStarted?.toLowerCase()
        ].includes(provider.reviewStatusCode?.toLowerCase()) &&
        reviewers?.length > 0 &&
        statusesByRole && Array.isArray(statusesByRole) &&
        statusesByRole.includes(provider.reviewStatusCode)
      ) {
        canStart = reviewers.some(
          (r: any) =>
            r.reviewStatusCode?.toLowerCase() ===
              provider.reviewStatusCode?.toLowerCase() &&
            (!r.reviewState ||
              r.reviewState?.toLowerCase() ===
                ReviewState.NOTSTART?.toLowerCase())
        );
      }

      setCanStartReview(canStart);
  }, [provider?.reviewStatusCode, canReview, reviewers, statusesByRole]);

  // Initialize provider from location
  useEffect(() => {
    const initializeProvider = async () => {
      const mpiId = searchParams.get("mpi");
      if (reviewYear && !mpiId && !providerInitializedRef.current) {
        const _provider = location.state?.provider;
        if (_provider) {
          providerInitializedRef.current = true;
          fetchProviderDetails(_provider?.mpi);
          fetchAllCommentsForProvider(_provider?.mpi);
          void fetchAllCommentsForProviderLevel(_provider?.mpi);
          setProviderId(_provider?.providerId);
          setCommentProviderMPI(_provider?.mpi);
          setCommentMeasureNumber(_provider?.mpi);
          getProviderAdditionalInfo(_provider?.mpi);
          setCommentMeasureId(0);
          setCommentLevel("provider");
          fetchStatusesByUser();
          getReviewersDetails(_provider?.mpi);
          canHaveAccessToReview(_provider?.mpi);
          fetchProviderContactDetails(_provider?.mpi);
          //handling mpi id
          //fetchProviderAttestationFileDetails(_provider?.mpi);
          // fetchPerformanceStandardsMeasuresInfo(
          //   _provider?.mpi,
          //   _provider?.appliedTier
          // );
        } // Fetch performance areas and related data
      } else if (reviewYear && mpiId && !providerInitializedRef.current) {
        providerInitializedRef.current = true;
        await fetchProviderDetails(mpiId);
        fetchAllCommentsForProvider(mpiId);
        void fetchAllCommentsForProviderLevel(mpiId);
        setProviderId(provider?.providerId);
        setCommentProviderMPI(mpiId);
        setCommentMeasureNumber(mpiId);
        getProviderAdditionalInfo(mpiId);
        setCommentMeasureId(0);
        setCommentLevel("provider");
        fetchStatusesByUser();
        getReviewersDetails(mpiId);
        canHaveAccessToReview(mpiId);
        fetchProviderContactDetails(mpiId);
      }
    };
    initializeProvider();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.provider, reviewYear, searchParams]);

  const fetchStatusesByUser = () => {
    if (statusesByUserCalledRef.current) {
      return;
    }

    statusesByUserCalledRef.current = true;

    httpService
      .get(`${ApiEndpoints.GetStatusesByRole}?providerType=${ProviderType.RS}`)
      .then((response: any) => {
        if (response.status === 200) {
          setStatusesByRole(response.data);
        }
      })
      .catch((error: any) => {
        statusesByUserCalledRef.current = false;
        console.error("[RHProviderProfileComponent] Failed to load statuses by user.", {
          message: error?.message,
          responseStatus: error?.response?.status,
          responseData: error?.response?.data,
          innerExceptionMessage: error?.cause?.message,
          stack: error?.stack
        });
      });
  };

  const isActiveReviewCycle = () => {
    if (isReviewCycleCalledRef.current) {
      return;
    }

    isReviewCycleCalledRef.current = true;

    httpService
      .get(`${ApiEndpoints.IsActiveReview}/${ProviderType.RS}`)
      .then((response: any) => {
        setIsActiveReview(response?.data);
      })
      .catch((error: any) => {
        isReviewCycleCalledRef.current = false;
        console.error("[RHProviderProfileComponent] Failed to load active review cycle.", {
          message: error?.message,
          responseStatus: error?.response?.status,
          responseData: error?.response?.data,
          innerExceptionMessage: error?.cause?.message,
          stack: error?.stack
        });
      });
  };

  const isActiveDisputeReviewCycle = () => {
    if (isDisputeCycleCalledRef.current) {
      return;
    }

    isDisputeCycleCalledRef.current = true;

    httpService
      .get(`${ApiEndpoints.IsActiveDisputeCycle}/${ProviderType.RS}`)
      .then((response: any) => {
        setIsActiveDisputeReview(response?.data);
      })
      .catch((error: any) => {
        isDisputeCycleCalledRef.current = false;
        console.error("[RHProviderProfileComponent] Failed to load active dispute cycle.", {
          message: error?.message,
          responseStatus: error?.response?.status,
          responseData: error?.response?.data,
          innerExceptionMessage: error?.cause?.message,
          stack: error?.stack
        });
      });
  };

  const getAllStatus = () => {
    if (allStatusCalledRef.current) {
      return;
    }

    allStatusCalledRef.current = true;

    httpService
      .get<Status[]>(`${ApiEndpoints.GetStatus}?providerType=${Constants.RSKey}`)
      .then((response) => {
        setAllStatues(response.data);
        setIsStatusLoaded(true);
      })
      .catch((error: any) => {
        allStatusCalledRef.current = false;
        console.error("[RHProviderProfileComponent] Failed to load all statuses.", {
          message: error?.message,
          responseStatus: error?.response?.status,
          responseData: error?.response?.data,
          innerExceptionMessage: error?.cause?.message,
          stack: error?.stack
        });
      });
  };

  //onload events

  useEffect(() => {
    isActiveReviewCycle();
    isActiveDisputeReviewCycle();
    getAllStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!provider?.providerId || !isStatusLoaded || !provider?.reviewStatus) {
      return;
    }
    
    const requiredStatuses = isActiveDisputeReview 
      ? updateStatusDropDownForDispute() 
      : updateStatusDropDown();
    
    setStatus(requiredStatuses);
    setSelectedStatus(getSelectedStatus(provider.reviewStatus));
  }, [provider?.providerId, provider?.reviewStatus, isStatusLoaded, isActiveDisputeReview, updateStatusDropDownForDispute, updateStatusDropDown, getSelectedStatus]);

  useEffect(() => {
    if (
      !appUser ||
      (!isSmeReviewer() && !isAdministrator()) ||
      !provider?.reviewStatusCode ||
      !reviewers || reviewers.length === 0 ||
      !performanceAreas || performanceAreas.length === 0 ||
      !changingStatus
    ) {
      return;
    }

    const isReviewCompleted = isActiveDisputeReview 
      ? provider.reviewStatusCode.toLowerCase() === ReviewStatusCode.RsResolutionReviewCompleted.toLowerCase()
      : provider.reviewStatusCode.toLowerCase() === ReviewStatusCode.RsReviewCompleted.toLowerCase();

    const isFinalReview = isActiveDisputeReview
      ? provider.reviewStatusCode.toLowerCase() === ReviewStatusCode.RsResolutionFinalReview.toLowerCase()
      : provider.reviewStatusCode.toLowerCase() === ReviewStatusCode.RsFinalReview.toLowerCase();

    if (!isReviewCompleted && !isFinalReview) {
      changeStatusToFinalReview();
    }
  }, [appUser, isSmeReviewer, isAdministrator, provider?.reviewStatusCode, reviewers, performanceAreas, isActiveDisputeReview, changeStatusToFinalReview]);

  useEffect(() => {
    if (providerId <= 0 || !allCommentData || !performanceAreas || performanceAreas.length === 0) {
      return;
    }
    updatePerformanceAreasForComments(0, true);
    // Intentionally exclude performanceAreas and updatePerformanceAreasForComments from deps:
    // updatePerformanceAreasForComments calls setPerformanceAreas (producing new refs),
    // which would recreate the callback and re-run this effect forever.
    // We only want this to sync when the underlying comment data or provider changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, allCommentData]);

  useEffect(() => {
    if (
      appUser &&
      provider &&
      reviewers &&
      reviewers.length > 0
    ) {
      // get the final reviewer details
      const finalReview = reviewers.find(
        (r: any) =>
          r.reviewStatusCode?.toLowerCase() ===
          ReviewStatusCode.RsFinalReview.toLowerCase()
      );
      if (
        finalReview
      ) {
        calculate(true);
        GetProviderOutComeStatus();
      }
    }
  }, [appUser, provider, reviewers]);

  useEffect(() => {
    if (allStatues && allStatues.length > 0 && status && status.length > 0 && performanceAreas && performanceAreas.length > 0) {
      handleRequiredStatusOption();
    }
  }, [performanceAreas, allStatues, handleRequiredStatusOption]);

    // useEffect(()=>{
    //   if(provider?.reviewStatusCode?.toLowerCase() == ReviewStatusCode.RsReviewCompleted.toLowerCase()){
    //     GetProviderOutComeStatus();
    //   }
    // },[provider]);

  const canHaveAccessToReview = (mpi: string) => {
    if (!mpi) {
      return;
    }

    httpService
      .get(`${ApiEndpoints.CanHaveAccessToReview}/${reviewYear}/${mpi}`)
      .then((response: any) => {
        if (response.status === 200) {
          setCanReview(response.data);
        }
      })
      .catch((error: any) => {
        console.error("[RHProviderProfileComponent] Failed to check review access.", {
          mpi,
          reviewYear,
          message: error?.message,
          responseStatus: error?.response?.status,
          responseData: error?.response?.data,
          innerExceptionMessage: error?.cause?.message,
          stack: error?.stack
        });
      });
  };

  const getProviderAdditionalInfo = (providerMpi: string) => {
    httpService
      .get(`${ApiEndpoints.GetProviderAdditionalInfo}/${reviewYear}/${providerMpi}`)
      .then((response: any) => {
        if (response.status === 200) {
          setProviderAdditionalInfo(response.data);
        }
      })
      .catch((error: any) => {
        console.error("[RHProviderProfileComponent] Failed to load provider additional info.", {
          providerMpi,
          reviewYear,
          message: error?.message,
          responseStatus: error?.response?.status,
          responseData: error?.response?.data,
          innerExceptionMessage: error?.cause?.message,
          stack: error?.stack
        });
      });
  };

  const canDisableStatusRelatedInputs = useCallback(() => {
    const hasValidStatus =
      selectedStatus &&
      statusesByRole &&
      Array.isArray(statusesByRole) &&
      statusesByRole.some(
        (s) => s?.toLowerCase() == selectedStatus.statusCode?.toLowerCase()
      );
    return !canReview || canStartReview || !hasValidStatus;
  }, [selectedStatus, statusesByRole, canReview, canStartReview]);

  const setReviewStatus = (e: Status) => {
    const getStatusChangeAlertConfig = (statusValue: any) => {
      const commonConfig = {
        open: true,
        title: "Confirm Status Change",
        message: `Are you sure you want to change the status to "${statusValue.statusName}"?`,
        onClose: (result: boolean) => {
          if (result) {
            updateStatus(statusValue);
          }
          dialogRef.current.setDialogConfig(undefined);
        },
      };

      if (
        statusValue.statusCode?.toLowerCase() === ReviewStatusCode.RsSMEReview.toLowerCase() ||
        statusValue.statusCode?.toLowerCase() === ReviewStatusCode.RsResolutionSMEReview.toLowerCase()
      ) {
        if (hasUnReviewedMeasures()) {
          return {
            open: true,
            hideNo: true,
            yesButtonLabel: "Ok",
            title: "Review Pending",
            message: "All measures must be reviewed before moving to SME Review.",
            onClose: () => {
              dialogRef.current.setDialogConfig(undefined);
            },
          };
        }
      }

      if (
        statusValue.statusCode?.toLowerCase() === ReviewStatusCode.RsFinalReview.toLowerCase() ||
        statusValue.statusCode?.toLowerCase() === ReviewStatusCode.RsResolutionFinalReview.toLowerCase()
      ) {
        if (hasAnySMEReviewForMeasure()) {
          return {
            open: true,
            hideNo: true,
            yesButtonLabel: "Ok",
            title: "SME Review Pending",
            message: "Some measures are pending SME review. You cannot move to Final Review until all SME reviews are completed.",
            onClose: () => {
              dialogRef.current.setDialogConfig(undefined);
            },
          };
        }

        if (hasUnReviewedMeasures()) {
          return {
            open: true,
            hideNo: true,
            yesButtonLabel: "Ok",
            title: "Review Pending",
            message: "All measures must be reviewed before moving to Final Review.",
            onClose: () => {
              dialogRef.current.setDialogConfig(undefined);
            },
          };
        }
      }

      if (
        statusValue.statusCode?.toLowerCase() === ReviewStatusCode.RsReviewCompleted.toLowerCase() ||
        statusValue.statusCode?.toLowerCase() === ReviewStatusCode.RsResolutionReviewCompleted.toLowerCase()
      ) {
        if (
          hasUnReviewedMeasures() ||
          (!calculatedTier &&
            provider.currentTierCode?.toLowerCase() === TierCode.UnAssigned.toLowerCase())
        ) {
          return {
            open: true,
            hideNo: true,
            yesButtonLabel: "Ok",
            title: hasUnReviewedMeasures() ? "Unreviewed Measures Present" : "Unassigned Tier",
            message: hasUnReviewedMeasures()
              ? "There are some unreviewed measures, so you cannot mark this as review complete."
              : `You cannot change the status to "${statusValue.statusName}" while the provider is in the Unassigned tier.`,
            onClose: () => {
              dialogRef.current.setDialogConfig(undefined);
            },
          };
        }
      }

      return commonConfig;
    };

    if (dialogRef.current) {
      const config = getStatusChangeAlertConfig(e);
      dialogRef.current.setDialogConfig(config);
    }
  };

  // const syncReviewStatusInIntakePortal = (statusId: any) => {
  //   httpService
  //     .post(ApiEndpoints.SyncProviderReviewStatusWithIntake, {
  //       ProviderId: provider.providerId,
  //       StatusId: statusId,
  //       reviewerEmail: appUser?.email ?? appUser?.name,
  //       MpiId: provider.mpi,
  //     })
  //     .then((response: any) => {
  //       if (response.status === 200) {
  //       }
  //     });
  // };

  // Fetch file information after file is uploaded
  useEffect(() => {
    if (fileUploaded) {
      //fetchSupportingFileInfo();
      setFileUploaded(false); // Reset after fetching
    }
  }, [fileUploaded]); // Triggers when a file is uploaded

  const fetchProviderDetails = async (mpi: string | number) => {
    httpService
      .get(`${ApiEndpoints.GetProviderDetails}/${reviewYear}/${mpi}`)
      .then((response: any) => {
        const currentProvider = response.data;
        setSelectedProvider(currentProvider);
        setSelectedStatus(getSelectedStatus(currentProvider.reviewStatus));
      })
      .catch((error: any) => {
        console.error("[RHProviderProfileComponent] Failed to load provider details.", {
          mpi,
          reviewYear,
          message: error?.message,
          responseStatus: error?.response?.status,
          responseData: error?.response?.data,
          innerExceptionMessage: error?.cause?.message,
          stack: error?.stack
        });
      });
  };

  const fetchProviderContactDetails = async (mpi: string | number)=>{
    httpService
      .get(`${ApiEndpoints.GetAgencyContactDetails}/${mpi}`)
      .then((response: any) => {
        setProviderContactDetails(response.data);
      })
      .catch((error: any) => {
        console.error("[RHProviderProfileComponent] Failed to load provider contact details.", {
          mpi,
          message: error?.message,
          responseStatus: error?.response?.status,
          responseData: error?.response?.data,
          innerExceptionMessage: error?.cause?.message,
          stack: error?.stack
        });
      });
  };

  const fetchAllCommentsForProvider = async (providerMpi: string) => {
    httpService
      .post(ApiEndpoints.GetAllCommentsForProvider, {
        commentLevel: commentLevel,
        providerMPI: providerMpi,
        measureId: commentMeasureId,
        reviewYear: reviewYear,
        providerType: ProviderType.RS
      })
      .then((response: any) => {
        if (response.status === 200) {
          setAllCommentData(response?.data);
        }
      })
      .catch((error: any) => {
        console.error("[RHProviderProfileComponent] Failed to load all comments for provider.", {
          providerMpi,
          commentLevel,
          commentMeasureId,
          reviewYear,
          message: error?.message,
          responseStatus: error?.response?.status,
          responseData: error?.response?.data,
          innerExceptionMessage: error?.cause?.message,
          stack: error?.stack
        });
      });
  };

  const fetchAllCommentsForProviderLevel = useCallback(async (providerMpi: string) => {
    if (!providerMpi) {
      return;
    }

    try {
      const response: any = await httpService.post(ApiEndpoints.GetAllComments, {
        commentLevel: "provider",
        providerMPI: providerMpi,
        measureId: 0,
        reviewYear,
        providerType: ProviderType.RS
      });

      if (response?.status === 200) {
        setAllProviderLevelCommentData(
          Array.isArray(response?.data) ? response.data : []
        );
      }
    } catch (error: any) {
      console.error("[RHProviderProfileComponent] Failed to load provider-level comments.", {
        providerMpi,
        reviewYear,
        message: error?.message,
        responseStatus: error?.response?.status,
        responseData: error?.response?.data,
        stack: error?.stack
      });
    }
  }, [reviewYear]);

  const updatePerformanceAreasStatus = useCallback((response: {
    reviewResponseId: number | null | undefined;
    performanceareaId: number;
    standardId: number;
    measureId: number;
    questionId: number;
    reviewResponse: string | null;
    reasons: any[];
    userReason: string;
  }) => {
    const isSmeResponse = response.reviewResponse?.toLowerCase() === "sme";

    if (response.measureId) {
      try {
        const cacheKey = `RS_SME_INVOLVED_MEASURES_${reviewYear}_${provider?.mpi || ""}`;
        const cachedIds = JSON.parse(sessionStorage.getItem(cacheKey) || "[]");
        const measureIds = Array.isArray(cachedIds) ? cachedIds.map(Number).filter(Number.isFinite) : [];
        const responseMeasureId = Number(response.measureId);
        const nextMeasureIds = isSmeResponse
          ? Array.from(new Set([...measureIds, responseMeasureId]))
          : measureIds.filter((id: number) => id !== responseMeasureId);

        sessionStorage.setItem(cacheKey, JSON.stringify(nextMeasureIds));
      } catch (error) {
        console.warn("[RHProviderProfileComponent] Failed to update SME flag session storage.", error);
      }
    }

    if (performanceAreas.length > 0) {
      setPerformanceAreas((prevPerformanceAreas) =>
        prevPerformanceAreas.map((area) => ({
          ...area,
          standards: area.standards.map((standard) => ({
            ...standard,
            measures: standard.measures.map((measure) => {
              if (measure.measureId === response.measureId) {
                if (!response.questionId) {
                  return {
                    ...measure,
                    reviewResponse: response.reviewResponse,
                    hasReasons:
                      (response.reasons && response.reasons.length > 0) ||
                      !!response.userReason,
                    isSmeInvolved: isSmeResponse,
                  };
                } else {
                  return {
                    ...measure,
                    questions: measure.questions.map((question) =>
                      question.questionId === response.questionId
                        ? {
                            ...question,
                            reviewResponse: response.reviewResponse,
                          }
                        : question
                    ),
                  };
                }
              }
              return measure;
            }),
          })),
        }))
      );
    }
  }, [performanceAreas, provider?.mpi, reviewYear]);

  const onReviewChange = useCallback((response: {
    reviewResponseId: number | null | undefined;
    performanceareaId: number;
    standardId: number;
    measureId: number;
    questionId: number;
    reviewResponse: string;
    reasons: any[];
    userReason: string;
  }) => {
    updatePerformanceAreasStatus(response);
    const existingIndex = reviweResponses.findIndex(
      (resp) =>
        resp.measureId === response.measureId &&
        resp.questionId === response.questionId
    );
    // Build an updated array immutably so React reliably detects the change
    let updatedResponses: ReviewRquest[];
    if (existingIndex >= 0) {
      updatedResponses = reviweResponses.map((resp, idx) =>
        idx === existingIndex
          ? {
              ...resp,
              reviewResponse: response.reviewResponse,
              reasons: response.reasons,
              userReason: response.userReason,
            }
          : resp
      );
    } else {
      updatedResponses = [
        ...reviweResponses,
        {
          tierId: performanceAreas[0].standards[0].tierId,
          reviewResponseId: response.reviewResponseId,
          mpiId: provider.mpi,
          measureId: response.measureId,
          questionId: response.questionId,
          reviewResponse: response.reviewResponse,
          reasons: response.reasons,
          userReason: response.userReason,
        },
      ];
    }
    setReviewResponses(updatedResponses);
    
    // Send only the current modified review response to the API
    const currentReviewResponse = {
      tierId: performanceAreas[0].standards[0].tierId,
      reviewResponseId: response.reviewResponseId,
      mpiId: provider.mpi,
      measureId: response.measureId,
      questionId: response.questionId,
      reviewResponse: response.reviewResponse,
      reasons: response.reasons,
      userReason: response.userReason,
    };

    saveReviewResponses(currentReviewResponse);
  }, [performanceAreas, provider, reviweResponses, updatePerformanceAreasStatus]);

  const calculateTier = useCallback(() => {
    if (!provider || !provider.providerId || !provider.mpi || !appliedTierId) {
      setIsCalculate(false);
      return;
    }

    if (isCalculate) {
      return;
    }

    setIsCalculate(true);
    GetProviderOutComeStatus();

    httpService
      .post(
        `${ApiEndpoints.SubmitPerformanceEvaluation}/${provider.providerId}/${appliedTierId}/${provider.mpi}?providerType=${Constants.RSKey}`,
        reviweResponses || []
      )
      .then((response: any) => {
        if (response?.data) {
          setCalculatedTier(response.data);
        }
        setIsCalculate(false);
      })
      .catch((error: any) => {
        setIsCalculate(false);
        console.error("[RHProviderProfileComponent] Failed to calculate tier.", {
          providerId: provider?.providerId,
          appliedTierId,
          mpi: provider?.mpi,
          message: error?.message,
          responseStatus: error?.response?.status,
          responseData: error?.response?.data,
          innerExceptionMessage: error?.cause?.message,
          stack: error?.stack
        });
      });
  }, [provider, appliedTierId, reviweResponses, isCalculate]);

  const calculate = useCallback((isOnload: boolean) => {
    if (
      provider?.reviewStatusCode?.toLowerCase() !== ReviewStatusCode.RsReviewCompleted.toLowerCase() &&
      provider?.reviewStatusCode?.toLowerCase() !== ReviewStatusCode.RsSMEReview.toLowerCase()
    ) {
      if ((isOnload ||

          (performanceAreas &&
            performanceAreas.length > 0 &&
            reviewers &&
            reviewers.length > 0))
      ) {
        const finalReview = reviewers.find(
          (r: any) =>
            r.reviewStatusCode?.toLowerCase() === provider.reviewStatusCode?.toLowerCase()
        );

        if (
          !hasUnReviewedMeasures() &&
          finalReview
        ) {
          calculateTier();
        }
      }
    } else {
      setIsCalculate(false);
    }
  }, [provider?.reviewStatusCode, isFinalReviewer, isAdministrator, performanceAreas, reviewers, hasUnReviewedMeasures, calculateTier]);

  const saveReviewResponses = useCallback((currentReviewResponse?: any) => {
    if (!appliedTierId || !provider || !provider.mpi) {
      return;
    }

    const dataToSend = currentReviewResponse ? [currentReviewResponse] : (reviweResponses || []);

    httpService
      .post(
        `${ApiEndpoints.SavePerformanceEvaluation}/${appliedTierId}/${provider.mpi}/${ProviderType.RS}`,
        dataToSend
      )
      .then(() => {
        if (isSmeReviewer() || isAdministrator()) {
          setChangingStatus(true);
          getReviewersDetails(provider.mpi);
        }
        calculate(false);
      })
      .catch((error: any) => {
        console.error("[RHProviderProfileComponent] Failed to save review responses.", {
          appliedTierId,
          mpi: provider?.mpi,
          message: error?.message,
          responseStatus: error?.response?.status,
          responseData: error?.response?.data,
          innerExceptionMessage: error?.cause?.message,
          stack: error?.stack
        });
      });
  }, [appliedTierId, provider, reviweResponses, isSmeReviewer, isAdministrator, getReviewersDetails, calculate]);

  const getFilenameFromContentDisposition = useCallback((contentDisposition: string) => {
    if (!contentDisposition) {
      return "";
    }
    const matches = contentDisposition.match(
      /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i
    );
    return matches && matches[1] ? decodeURIComponent(matches[1]) : "";
  }, []);

  const downLoadProviderDataByDataSource = useCallback((dataSource: string) => {
    httpService
      .getBlob(`${ApiEndpoints.ExtractDataByDataSource}/${provider.mpi}/${dataSource}?reviewYear=${reviewYear}`)
      .then((response: any) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        const contentDisposition = response.headers["content-disposition"] ?? "";
        link.setAttribute("download", getFilenameFromContentDisposition(contentDisposition));
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((error: any) => {
        console.error("[RHProviderProfileComponent] Failed to download provider data source file.", {
          mpi: provider?.mpi,
          dataSource,
          reviewYear,
          message: error?.message,
          responseStatus: error?.response?.status,
          responseData: error?.response?.data,
          innerExceptionMessage: error?.cause?.message,
          stack: error?.stack
        });
        setShowConfirmDialog(true);
      });
  }, [provider.mpi, reviewYear, getFilenameFromContentDisposition]);

  const fetchPerformanceStandardsMeasuresInfo = async (
    mpiId: number,
    providerAppliedTier: string
  ) => {
    try {
      if (!mpiId || !providerAppliedTier) {
        setLoading(false);
        return;
      }

      setLoading(true);
      const performanceAreaUrl = isActiveDisputeReview
        ? ApiEndpoints.GetAllPerformanceStandardsMeasuresDisputeInfo
        : ApiEndpoints.GetAllPerformanceStandardsMeasuresInfo;

      const response = await httpService.get<PerformanceArea[]>(
        `${performanceAreaUrl}/${reviewYear}/${mpiId}?tierCode=${providerAppliedTier}`
      );

      const tierData = groupDataByTier(response.data);
      const activeTierData = tierData.filter(
        (tier) => tier.tierCode?.toLowerCase() === providerAppliedTier?.toLowerCase()
      )[0];

      setPerformanceAreas(activeTierData?.performanceAreas || []);
      setAppliedTierId(activeTierData?.tierId || 0);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      console.error("[RHProviderProfileComponent] Failed to load performance standards and measures.", {
        mpiId,
        providerAppliedTier,
        reviewYear,
        message: error?.message,
        responseStatus: error?.response?.status,
        responseData: error?.response?.data,
        innerExceptionMessage: error?.cause?.message,
        stack: error?.stack
      });
    }
  };

  const groupDataByTier = useCallback((data: any[]): TierQuestions[] => {
    const tierMap: { [key: number]: TierQuestions } = {};
    const smeSessionMeasureIds = new Set<number>();

    try {
      const cacheKey = `RS_SME_INVOLVED_MEASURES_${reviewYear}_${provider?.mpi || ""}`;
      const cachedIds = JSON.parse(sessionStorage.getItem(cacheKey) || "[]");

      if (Array.isArray(cachedIds)) {
        cachedIds.forEach((id: any) => {
          const parsedId = Number(id);
          if (!Number.isNaN(parsedId)) {
            smeSessionMeasureIds.add(parsedId);
          }
        });
      }
    } catch (error) {
      console.warn("[RHProviderProfileComponent] Failed to read SME flag from session storage.", error);
    }

    data.forEach((item) => {
      const {
        tierId,
        tierName,
        performanceAreaId,
        performanceAreaTitle,
        performanceAreaDescription,
        standardId,
        standardTitle,
        standardDescription,
        measureId,
        measureTitle,
        measureDescription,
        measureNumber,
        reviewResponse,
        hasReason,
        isReadOnly,
        reviewResponseId,
        isCalculatedField,
        questionId,
        questionTitle,
        questionDescription,
        isReviewed,
        fieldName,
        fieldValue,
        dataFieldType,
        dataSource,
        hasValidThreshold,
        questionSequence,
        questionNumber,
        propertySequence,
        displayConfig,
        recommendedStatus,
        comment,
        attachments,
        isEnabledForSme
      } = item;

      const measureIdNumber = Number(measureId);
      const normalizedReviewResponse = reviewResponse?.toLowerCase();
      const shouldShowSmeFlag = normalizedReviewResponse === "sme";

      if (!Number.isNaN(measureIdNumber)) {
        if (shouldShowSmeFlag) {
          smeSessionMeasureIds.add(measureIdNumber);
        } else {
          smeSessionMeasureIds.delete(measureIdNumber);
        }
      }

      if (!tierMap[tierId]) {
        tierMap[tierId] = {
          tierId,
          tierName,
          tierCode: item.tierCode,
          performanceAreas: [],
        };
      }

      if (!tierMap[tierId].performanceAreas) {
        tierMap[tierId].performanceAreas = [];
      }

      let performanceArea = tierMap[tierId].performanceAreas.find(
        (pa) => pa.performanceAreaId === performanceAreaId
      );

      if (!performanceArea) {
        performanceArea = {
          performanceAreaId,
          performanceAreaTitle,
          performanceAreaDescription,
          standards: [],
        };
        tierMap[tierId].performanceAreas.push(performanceArea);
      }

      let standard = performanceArea.standards.find(
        (s) => s.standardId === standardId
      );

      if (!standard) {
        standard = {
          standardId,
          standardTitle,
          standardDescription,
          isLocationSpecific: item.isLocationSpecific,
          tierId,
          tierName,
          measures: [],
        };
        performanceArea.standards.push(standard);
      }

      let measure = standard.measures.find((m) => m.measureId == measureId);

      if (!measure) {
        const commentAvailable =
          allCommentData.filter((c) => c.measureId === measureId).length > 0;

        measure = {
          measureId,
          measureTitle,
          measureDescription,
          measureNumber,
          reviewResponse,
          hasReasons: hasReason,
          isReadOnly: isReadOnly,
          reviewResponseId: reviewResponse ? reviewResponseId : null,
          questions: [],
          isCommentsAvailable: commentAvailable,
          recommendedStatus,
          isEnabledForSme: isEnabledForSme === true,
          isSmeInvolved: shouldShowSmeFlag
        };
        standard.measures.push(measure);
      }

      if (isEnabledForSme === true) {
        measure.isEnabledForSme = true;
      }

      measure.isSmeInvolved = shouldShowSmeFlag;

      let question = measure.questions.find((q) => q.questionId === questionId);

      if (!question) {
        question = {
          questionId,
          questionTitle,
          questionDescription,
          questionSequence: questionSequence,
          questionNumber: questionNumber,
          dataFieldType,
          isReviewed,
          dataSource,
          reviewResponseId: isReviewed ? reviewResponseId : null,
          isCalculatedField,
          comment,
          attachments: safeJsonParse(attachments, []),
          displayConfig: safeJsonParse(displayConfig, null),
          responseData: [],
        };

        measure.questions.push(question);
      }

      question.responseData.push({
        fieldName,
        fieldValue,
        hasValidThreshold,
        propertySequence,
      });
    });

    try {
      const cacheKey = `RS_SME_INVOLVED_MEASURES_${reviewYear}_${provider?.mpi || ""}`;
      sessionStorage.setItem(cacheKey, JSON.stringify(Array.from(smeSessionMeasureIds)));
    } catch (error) {
      console.warn("[RHProviderProfileComponent] Failed to update SME flag session storage.", error);
    }

    return Object.values(tierMap);
  }, [allCommentData, safeJsonParse, provider?.mpi, reviewYear]);

  const canDisable = useCallback(() => {
    return (
      !appUser ||
      //isAdministrator() ||
      selectedStatus?.statusCode?.toLowerCase() ==
        ReviewStatusCode.NotStarted?.toLowerCase() ||
      selectedStatus?.statusCode?.toLowerCase() ==
        ReviewStatusCode.RsNotStarted?.toLowerCase() ||
      provider?.reviewStatusId == 0 ||
      selectedStatus?.statusCode?.toLowerCase() == ReviewStatusCode.RsReviewCompleted?.toLowerCase() ||
      selectedStatus?.statusCode?.toLowerCase() == ReviewStatusCode.RsResolutionReviewCompleted?.toLowerCase() ||
      selectedStatus?.statusCode?.toLowerCase() == ReviewStatusCode.RsResolutionNotStarted?.toLowerCase() ||
      canDisableStatusRelatedInputs()
    );
  }, [appUser, selectedStatus, provider?.reviewStatusId, canDisableStatusRelatedInputs]);

  const renderStandardsSection = useCallback((standards: Standard[]) => {
    return (
      <>
        {[...(standards || [])]
          .sort((a, b) => a?.standardTitle.localeCompare(b?.standardTitle))
          .map((standard: Standard) => (
            <StandardsDetailsComponent
              downLoadProviderDataByDataSource={
                downLoadProviderDataByDataSource
              }
              key={standard.standardId}
              isEnabled={isActiveReview && !canDisable()}
              standard={standard}
              dialogRef={dialogRef}
              onReviewChange={onReviewChange}
              updatePerformanceAreasForComments={updatePerformanceAreasForComments}
              providerAgencyName={provider?.providerAgencyName}
              evaluationStatuses={measureEvaluationStatuses}
              mpi={provider?.mpi}
              canDisableDropdown={(isActiveDisputeReview && !hasDisputeQuestion([standard]))}
              CanShowDisputeIcon={(isActiveDisputeReview && hasDisputeQuestion([standard]))}
              graduatedMeasures={graduateMeasures}
              appliedTier={provider.appliedTier}
              isActiveReview={isActiveReview}
              isActiveDisputeReview={isActiveDisputeReview}
              reviewStatusCode={provider?.reviewStatusCode}
              reviewYear={reviewYear}
              providerType={ProviderType.RS}
            />
          ))}
      </>
    );
  }, [isActiveReview,
    canDisable,
    dialogRef,
    onReviewChange,
    updatePerformanceAreasForComments,
    provider?.providerAgencyName,
    measureEvaluationStatuses,
    provider?.mpi,
    isActiveDisputeReview,
    graduateMeasures,
    provider?.appliedTier,
    downLoadProviderDataByDataSource,
    selectedStatus,
    canReview,
    canStartReview,
    statusesByRole,
    appUser,
    isAdministrator,
    isFinalReviewer,
    isSmeReviewer,
    isInitialReviewer
  ]);

  const getPAEvaluationStatusCounts = (area: PerformanceArea) => {
    const statuses = [...measureEvaluationStatuses];

    const counts = statuses
      .filter((s) => s.canShowStatus)
      .map((statusValue) => {
        return {
          ...statusValue,
          count: area.standards.reduce((acc, standard) => {
            return (
              acc +
              standard.measures.filter((measure) => {
                if (statusValue.code === "null" || statusValue.code?.toLowerCase() === "nr") {
                  return (
                    measure.reviewResponse === null ||
                    measure.reviewResponse === undefined ||
                    measure.reviewResponse === "" ||
                    (typeof measure.reviewResponse === "string" &&
                      (
                        measure.reviewResponse?.trim().toLowerCase() === "null" ||
                        measure.reviewResponse?.trim().toLowerCase() === "nr"
                      ))
                  );
                }
                return (
                  measure.reviewResponse &&
                  measure.reviewResponse?.toLowerCase() === statusValue.code?.toLowerCase()
                );
              }).length
            );
          }, 0),
        };
      });

    const totalMeasuresCount = area.standards.reduce(
      (acc, standard) => acc + standard.measures.length,
      0
    );

    return (
      <span style={{ marginLeft: 8 }}>
        {counts
          .filter((statusValue) => statusValue.code === "nr" || statusValue.code === "sme")
          .map((statusValue) => (
            <Badge
              key={statusValue.code}
              value={
                statusValue.code === "nr"
                  ? `Unreviewed Measures: ${statusValue.count}`
                  : `SME Review Measures: ${statusValue.count} / ${totalMeasuresCount}`
              }
              severity={statusValue.code === "nr" ? "danger" : "warning"}
              className="ml-2"
            />
          ))}
      </span>
    );
  };

  const perfromanceAreaTabHeader = (area: PerformanceArea) => {
    const getIsCommentAvailable = (standards: Standard[]): boolean => {
      let isComments: boolean = false;
      standards.forEach((std) =>
        std.measures.forEach((measure) => {
          if (measure.isCommentsAvailable) {
            isComments = true;
          }
        })
      );
      return isComments;
    };

    const hasJustificationReasons = (standards: Standard[]): boolean => {
      let hasReasons: boolean = false;
      standards.forEach((std) =>
        std.measures.forEach((measure) => {
          if (measure.reviewResponse?.toLowerCase() === "na") {
            hasReasons = true;
            return hasReasons;
          }
        })
      );
      return hasReasons;
    };

    const hasSmeInvolvedMeasure = (standards: Standard[]): boolean => {
      return standards.some((std) =>
        std.measures.some((measure) =>
          measure.reviewResponse?.toLowerCase() === "sme" ||
          measure.isSmeInvolved === true
        )
      );
    };

    return (
      <div className="d-flex">
        <div
          className="d-flex"
          style={{ flexGrow: "1", justifyContent: "space-between" }}
        >
          <Tooltip target={"#pa-" + area.performanceAreaId} />
          <div
            data-pr-tooltip={area.performanceAreaTitle}
            data-pr-position="left"
            data-pr-at="right top-20"
            data-pr-my="center"
            id={area.performanceAreaId.toString()}
            className={""}
          >
            <div
              style={{
                display: "inline-block",
                maxWidth: "100ch",
                lineHeight: "2",
                textOverflow: "ellipsis",
              }}
            >
              {area.performanceAreaTitle}
              {": "}
              {area.performanceAreaDescription}
            </div>
            <span style={{ marginLeft: 8 }}>
              {hasDisputeQuestion(area.standards) ? (
                <i
                  className="pi pi-flag-fill"
                  style={{ width: "20px", paddingTop: "5px", color: "red", fontSize: "1.2rem" }}
                  title="Dispute Performance Area"
                ></i>
              ) : ""}
              <Tooltip target=".pi-exclamation-circle" />
            </span>
          </div>
          {getPAEvaluationStatusCounts(area)}
        </div>
        <div>
          {hasSmeInvolvedMeasure(area.standards) && (
            <i
              className="pi pi-flag-fill"
              style={{ color: "#FF9800", fontSize: "1.2rem", width: "20px", paddingTop: "5px" }}
              title="SME Review"
            />
          )}
        </div>
        <div>
          {hasJustificationReasons(area.standards) ? (
            <i
              className="pi pi-comments measure-not-approved-icon"
              style={{ width: "20px", paddingTop: "5px" }}
              title="Fail Reasons"
            ></i>
          ) : ""}
        </div>
        <div>
          {getIsCommentAvailable(area.standards) ? (
            <i
              className={"pi pi-comments"}
              style={{ width: "20px", paddingTop: "5px" }}
              title="Comments"
            ></i>
          ) : (
            <i className="pi" style={{ width: "20px", paddingTop: "5px" }}></i>
          )}
        </div>
      </div>
    );
  };

  const hasDisputeQuestion = useCallback((standards: Standard[]) => {
    let hasDisputeRaised: boolean = false;
    hasDisputeRaised = standards.some(
      s => s.measures.some(
        m => m.questions.some(
          q => !!q.comment || (Array.isArray(q.attachments) && q.attachments.length > 0)
        )
      )
    );
    return hasDisputeRaised;
  }, []);
  
  // countTotalMeasures is already memoized; expose it directly without an extra callback wrapper.
  const getTotalMeasures = () => countTotalMeasures;

  // const getApprovalPercentage = useMemo(() => {
  //   const totalMeasures = countTotalMeasures;
  //   if (totalMeasures === 0) {
  //     return '0.00% Measures Reviewed';
  //   }
  //   const approvedMeasures = filterMeasures(m => 
  //     m?.reviewResponse && !isUnreviewed(m.reviewResponse)
  //   ).length;
  //   const approvalPercentage = (approvedMeasures / totalMeasures) * 100;
  //   return `${approvalPercentage.toFixed(2)}% Measures Reviewed`;
  // }, [countTotalMeasures, filterMeasures, isUnreviewed]);

  const getCountByStatus = useCallback((statusCode: string) => {
    if (statusCode === "null" || statusCode === "nr") {
      return filterMeasures((m) => isUnreviewed(m?.reviewResponse)).length;
    }
    return filterMeasures((m) => m?.reviewResponse?.toLowerCase() === statusCode.toLowerCase()).length;
  }, [filterMeasures, isUnreviewed]);

  const renderEvaluationStatusByReview = () => {
    const statuses = [...measureEvaluationStatuses];

    return (
      <div>
        {statuses && statuses.length > 0 && (
          <>
            {statuses.filter((s) => s.code === "nr" && s.canShowStatus).map((statusValue) => (
              <Badge
                key={statusValue.code}
                value={`Unreviewed Measures: ${getCountByStatus("nr")} / ${getTotalMeasures()}`}
                severity="danger"
                style={{ fontWeight: 600 }}
                className="ml-2"
              />
            ))}
            {statuses.filter((s) => s.code === "a" && s.canShowStatus).map((statusValue) => (
              <Badge
                key={statusValue.code}
                value={`Approved Measures: ${getCountByStatus("a")} / ${getTotalMeasures()}`}
                severity="success"
                style={{ fontWeight: 600 }}
                className="ml-2"
              />
            ))}
            {statuses.filter((s) => s.code === "sme" && s.canShowStatus).map((statusValue) => (
              <Badge
                key={statusValue.code}
                value={`SME Review Measures: ${getCountByStatus("sme")} / ${getTotalMeasures()}`}
                severity="warning"
                style={{ fontWeight: 600 }}
                className="ml-2"
              />
            ))}
          </>
        )}
      </div>
    );
  };

  const renderPerformanceAreasAccordion = () => {
    return (
      <>
        <div>
          <div className="evaluation-info-container">
            <h3>
              <span className="section-title">Performance Evaluation</span>

              {/* <Badge
                value={getApprovalPercentage}
                severity={getApprovalPercentage?.includes("100") ? "success" : "info"}
                className="ml-2"
              /> */}
            </h3>
            {renderEvaluationStatusByReview()}
          </div>
          <Accordion>
            {performanceAreas
              .sort((a, b) => alphaNumericSort(a.performanceAreaTitle, b?.performanceAreaTitle))
              .map((area) => (
                <AccordionTab
                  key={area.performanceAreaTitle}
                  header={perfromanceAreaTabHeader(area)}
                  headerClassName={hasDisputeQuestion(area.standards) ? "dispute-accordion-header" : ""}
                >
                  {renderStandardsSection(area.standards)}
                </AccordionTab>
              ))}
          </Accordion>
        </div>
      </>
    );
  };

  const handleUploadSuccess = () => {
    setUploadStatus("File uploaded successfully");
    setFileUploaded(true);
  };

  const renderTierView = () => {
    return (
      <div className="tier-view-accordion">
        {loading ? (
          <div className="tier-loading-skeleton">
            <ProgressSpinner />
          </div>
        ) : (
          renderPerformanceAreasAccordion()
        )}
      </div>
    );
  };

  const downloadDataFile = (fileName: any, currentProviderId: any) => {
    if (!fileName || !currentProviderId) {
      setShowConfirmDialog(true);
      return;
    }

    const normalizedFileName = String(fileName)
      .normalize("NFKC")
      .replace(/[\u2010-\u2015\u2212]/g, "-");

    const encodedFileName = encodeURIComponent(normalizedFileName);

    httpService
      .get(
        `${ApiEndpoints.DownloadSupportingDataFile}/${currentProviderId}/${encodedFileName}?reviewYear=${reviewYear}`
      )
      .then((response: any) => {
        if (response?.data?.url) {
          const validatedUrl = new URL(response.data.url);

          if (
            validatedUrl.protocol === "http:" ||
            validatedUrl.protocol === "https:"
          ) {
            const anchor = document.createElement("a");
            anchor.href = validatedUrl.toString();
            anchor.target = "_blank";
            anchor.rel = "noopener noreferrer";
            anchor.click();
          }
        }
      })
      .catch((error: any) => {
        console.error(
          "[RHProviderProfileComponent] Failed to download attestation file.",
          {
            fileName,
            normalizedFileName,
            providerId: currentProviderId,
            reviewYear,
            message: error?.message,
            responseStatus: error?.response?.status,
            responseData: error?.response?.data,
            innerExceptionMessage: error?.cause?.message,
            stack: error?.stack,
          }
        );

        setShowConfirmDialog(true);
      });
  };

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const GetProviderOutComeStatus = () => {
    if (!provider?.mpi || !reviewYear) {
      return;
    }

    setOutcomeStatusLoading(true);
    setOutcomeStatusError(null);

    httpService
      .get(`${ApiEndpoints.GetReviewOutcome}/${reviewYear}/${provider.mpi}`)
      .then((response: any) => {
        if (response.status === 200 && response.data) {
          setOutcomeStatusTable(response.data);
        } else {
          setOutcomeStatusError("No data returned from API");
        }
      })
      .catch((error: any) => {
        console.error("[RHProviderProfileComponent] Failed to load provider outcome status.", {
          mpi: provider?.mpi,
          reviewYear,
          message: error?.message,
          responseStatus: error?.response?.status,
          responseData: error?.response?.data,
          innerExceptionMessage: error?.cause?.message,
          stack: error?.stack
        });
        setOutcomeStatusError("Failed to fetch outcome status table");
        setOutcomeStatusTable(null);
      })
      .finally(() => {
        setOutcomeStatusLoading(false);
      });
  };

  const hideDialog = () => {
    setShowConfirmDialog(false);
  };

  const StartReview = () => {
    httpService
      .get(`${ApiEndpoints.StartReview}/${reviewYear}/${provider.mpi}`)
      .then((response: any) => {
        if (response) {
          setCanStartReview(false);
          getReviewersDetails(provider.mpi);
        }
      })
      .catch((error: any) => {
        console.error("[RHProviderProfileComponent] Failed to start review.", {
          mpi: provider?.mpi,
          reviewYear,
          message: error?.message,
          responseStatus: error?.response?.status,
          responseData: error?.response?.data,
          innerExceptionMessage: error?.cause?.message,
          stack: error?.stack
        });
      });
  };

  useEffect(() => {
    if (!measureEvaluationStatusesCalledRef.current) {
      getMeasureEvaluationStatuses();
    }
    return () => {
      if (dialogRef.current) {
        dialogRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (appliedTierId && appliedTierId > 0) {
      getgraduateMeasures();
    }
  }, [appliedTierId]);

  useEffect(() => {
    if (!canReview || !reviewers || reviewers.length === 0 || !provider || !provider.reviewStatusCode) {
      return;
    }

    let canStart = false;
    if (
      ![
        ReviewStatusCode.RsNotStarted?.toLowerCase(),
        ReviewStatusCode.NotStarted?.toLowerCase(),
        ReviewStatusCode.RsResolutionNotStarted?.toLowerCase()
      ].includes(provider.reviewStatusCode?.toLowerCase()) &&
      reviewers?.length > 0 &&
      statusesByRole &&
      Array.isArray(statusesByRole) &&
      statusesByRole.includes(provider.reviewStatusCode)
    ) {
      canStart = reviewers.some(
        (r: any) =>
          r.reviewStatusCode?.toLowerCase() === provider.reviewStatusCode?.toLowerCase() &&
          (!r.reviewState ||
            r.reviewState?.toLowerCase() === ReviewState.NOTSTART?.toLowerCase())
      );
    }

    setCanStartReview(canStart);
  }, [provider?.reviewStatusCode, canReview, reviewers, statusesByRole]);

  useEffect(() => {
    const initializeProvider = async () => {
      const mpiId = searchParams.get("mpi");
      if (reviewYear && !mpiId && !providerInitializedRef.current) {
        const currentProvider = location.state?.provider;
        if (currentProvider) {
          providerInitializedRef.current = true;
          fetchProviderDetails(currentProvider?.mpi);
          fetchAllCommentsForProvider(currentProvider?.mpi);
          void fetchAllCommentsForProviderLevel(currentProvider?.mpi);
          setProviderId(currentProvider?.providerId);
          setCommentProviderMPI(currentProvider?.mpi);
          setCommentMeasureNumber(currentProvider?.mpi);
          getProviderAdditionalInfo(currentProvider?.mpi);
          setCommentMeasureId(0);
          setCommentLevel("provider");
          fetchStatusesByUser();
          getReviewersDetails(currentProvider?.mpi);
          canHaveAccessToReview(currentProvider?.mpi);
          fetchProviderContactDetails(currentProvider?.mpi);
        }
      } else if (reviewYear && mpiId && !providerInitializedRef.current) {
        providerInitializedRef.current = true;
        const mpi = Number(mpiId);
        await fetchProviderDetails(mpi);
        fetchAllCommentsForProvider(mpiId);
        setProviderId(provider?.providerId);
        setCommentProviderMPI(mpiId);
        setCommentMeasureNumber(mpiId);
        getProviderAdditionalInfo(mpiId);
        setCommentMeasureId(0);
        setCommentLevel("provider");
        fetchStatusesByUser();
        getReviewersDetails(mpiId);
        canHaveAccessToReview(mpiId);
        fetchProviderContactDetails(mpi);
      }
    };

    initializeProvider();
  }, [location.state?.provider, reviewYear, searchParams]);

  useEffect(() => {
    if (!provider?.providerId || !provider?.mpi || !provider?.appliedTierCode) {
      return;
    }

    setProviderId(provider.providerId);
    fetchPerformanceStandardsMeasuresInfo(provider.mpi, provider.appliedTierCode);
  }, [provider?.providerId, provider?.mpi, provider?.appliedTierCode]);

  useEffect(() => {
    isActiveReviewCycle();
    isActiveDisputeReviewCycle();
    getAllStatus();
  }, []);

  useEffect(() => {
    if (!provider?.providerId || !isStatusLoaded || !provider?.reviewStatus) {
      return;
    }

    const requiredStatuses = isActiveDisputeReview
      ? updateStatusDropDownForDispute()
      : updateStatusDropDown();

    setStatus(requiredStatuses);
    setSelectedStatus(getSelectedStatus(provider.reviewStatus));
  }, [provider?.providerId, provider?.reviewStatus, isStatusLoaded, isActiveDisputeReview, updateStatusDropDownForDispute, updateStatusDropDown, getSelectedStatus]);

  useEffect(() => {
    if (
      !appUser ||
      (!isSmeReviewer() && !isAdministrator()) ||
      !provider?.reviewStatusCode ||
      !reviewers || reviewers.length === 0 ||
      !performanceAreas || performanceAreas.length === 0 ||
      !changingStatus
    ) {
      return;
    }

    const isReviewCompleted = isActiveDisputeReview
      ? provider.reviewStatusCode.toLowerCase() === ReviewStatusCode.RsResolutionReviewCompleted.toLowerCase()
      : provider.reviewStatusCode.toLowerCase() === ReviewStatusCode.RsReviewCompleted.toLowerCase();

    const isFinalReview = isActiveDisputeReview
      ? provider.reviewStatusCode.toLowerCase() === ReviewStatusCode.RsResolutionFinalReview.toLowerCase()
      : provider.reviewStatusCode.toLowerCase() === ReviewStatusCode.RsFinalReview.toLowerCase();

    if (!isReviewCompleted && !isFinalReview) {
      changeStatusToFinalReview();
    }
  }, [appUser, isSmeReviewer, isAdministrator, provider?.reviewStatusCode, reviewers, performanceAreas, changingStatus, isActiveDisputeReview, changeStatusToFinalReview]);

  useEffect(() => {
    if (providerId <= 0 || !allCommentData || !performanceAreas || performanceAreas.length === 0) {
      return;
    }
    updatePerformanceAreasForComments(0, true);
  }, [providerId, allCommentData, performanceAreas, updatePerformanceAreasForComments]);

  useEffect(() => {
    if (appUser && provider && reviewers && reviewers.length > 0) {
      const finalReview = reviewers.find(
        (r: any) =>
          r.reviewStatusCode?.toLowerCase() === ReviewStatusCode.RsFinalReview.toLowerCase()
      );
      if (
        finalReview
      ) {
        calculate(true);
        GetProviderOutComeStatus();
      }
    }
  }, [appUser, provider, reviewers]);

  useEffect(() => {
    if (allStatues && allStatues.length > 0 && status && status.length > 0 && performanceAreas && performanceAreas.length > 0) {
      handleRequiredStatusOption();
    }
  }, [performanceAreas, allStatues, handleRequiredStatusOption]);

  useEffect(() => {
    if (fileUploaded) {
      setFileUploaded(false);
    }
  }, [fileUploaded]);

  const currentStatusCode = (
    selectedStatus?.statusCode ||
    provider?.reviewStatusCode ||
    ""
  ).toLowerCase();

  const isSMEReviewStage =
    currentStatusCode === ReviewStatusCode.RsSMEReview.toLowerCase() ||
    currentStatusCode === ReviewStatusCode.RsResolutionSMEReview.toLowerCase();

  const isFinalReviewStage =
    currentStatusCode === ReviewStatusCode.RsFinalReview.toLowerCase() ||
    currentStatusCode === ReviewStatusCode.RsResolutionFinalReview.toLowerCase() ||
    currentStatusCode === ReviewStatusCode.RsReviewCompleted.toLowerCase() ||
    currentStatusCode === ReviewStatusCode.RsResolutionReviewCompleted.toLowerCase();

  return (
    <div className="provider-profile-file-upload">
      {provider && (
        <>
          <div className="profile-top-actions">
            {Array.isArray(allProviderLevelCommentData) &&
              allProviderLevelCommentData.length > 0 && (
                <Button
                  icon="pi pi-comment"
                  className="p-button-text profile-icon-btn"
                  aria-label="View provider comments"
                  onClick={() => {
                    setCommentLevel("provider");
                    setCommentMeasureId(0);
                    setSidebarVisible(true);
                  }}
                />
              )}

            <Button
              className="p-button-text profile-comment-btn"
              disabled={!isActiveReview}
              onClick={() => {
                setCommentLevel("provider");
                setCommentMeasureId(0);
                setSidebarVisible(true);
              }}
            >
              Comment
            </Button>
          </div>

          <div className="profile-card">
            <div className="profile-card-title">Agency Information</div>
            <div className="profile-info-table-wrapper">
              <table className="profile-info-table">
                <thead>
                  <tr>
                    <th>Agency Name</th>
                    <th>Agency MPI</th>
                    <th>Tier Desired</th>
                    {providerAdditionalInfo &&
                      Object.keys(providerAdditionalInfo).map((key) => (
                        <th key={`h-${key}`}>{key}</th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      {provider?.providerAgencyName?.length > 40 ? (
                        <>
                          <span
                            className="agency-name-ellipsis"
                            data-pr-tooltip={provider?.providerAgencyName}
                          >
                            {provider?.providerAgencyName}
                          </span>
                          <Tooltip target=".agency-name-ellipsis" />
                        </>
                      ) : (
                        provider?.providerAgencyName
                      )}
                    </td>
                    <td>{provider?.mpi}</td>
                    <td>{provider?.desiredTier || provider?.appliedTier || "-"}</td>
                    {providerAdditionalInfo &&
                      Object.keys(providerAdditionalInfo).map((key) => (
                        <td key={`v-${key}`}>{providerAdditionalInfo[key]}</td>
                      ))}
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="profile-card-divider" />

            <div className="profile-card-title">Primary Contact Information</div>
            {loading ? (
              <p>Loading provider details...</p>
            ) : (
              <DataTable
                value={[providerContactDetails]}
                paginator={false}
                className="primary-contact"
              >
                <Column field="primaryContactFirstName" header="First Name" />
                <Column field="primaryContactLastName" header="Last Name" />
                <Column field="primaryContactPhone" header="Phone Number" />
                <Column
                  field="primaryContactEmail"
                  header="Email Address"
                />
              </DataTable>
            )}
          </div>
        </>
      )}

      <Dialog
        header="Upload"
        visible={visible}
        onHide={() => setVisible(false)}
      >
        <div
          className="flex flex-column px-8 py-5 gap-4"
          style={{
            borderRadius: "12px",
            width: "1000px",
            height: "auto",
            minHeight: "300px",
          }}
        >
          {provider.providerId && (
            <MultipartFileUploadComponent
              name="file"
              customUpload
              accept=".pdf,.doc,.docx"
              maxFileSize={1000000}
              auto
              chooseLabel="Choose"
              uploadLabel="Upload"
              cancelLabel="Cancel"
              className="p-mb-3"
              onFileuploaded={() => {
                if (provider && provider.mpi && provider.appliedTierCode) {
                  fetchPerformanceStandardsMeasuresInfo(
                    provider.mpi,
                    provider.appliedTierCode
                  );
                }
              }}
              providerId={provider.mpi}
              providerProfile="ProviderProfile"
              onUpload={handleUploadSuccess}
              onError={() => {
                setUploadStatus("Upload failed. Please try again.");
              }}
            />
          )}
          {uploadStatus && <p>{uploadStatus}</p>}
        </div>
      </Dialog>

      {provider &&
        (() => {
          const isSmePendingMeasure = (measure: Measure): boolean => {
            const reviewResponse = measure?.reviewResponse?.toLowerCase();

            if (isInitialReviewer()) {
              return reviewResponse === "sme";
            }

            if (isSmeReviewer()) {
              return (
                measure?.isEnabledForSme === true &&
                measure?.isSmeInvolved === true &&
                reviewResponse === "sme"
              );
            }

            if (isFinalReviewer() || isAdministrator()) {
              return (
                measure?.isSmeInvolved === true &&
                reviewResponse === "sme"
              );
            }

            return false;
          };

          const smePendingMeasuresCount = (performanceAreas || []).reduce(
            (areaCount, area) =>
              areaCount +
              (area?.standards || []).reduce(
                (standardCount, standard: any) =>
                  standardCount +
                  (standard?.measures || []).filter(isSmePendingMeasure).length,
                0
              ),
            0
          );

          const performanceAreasRemaining = (performanceAreas || []).filter(
            (area) =>
              area?.standards?.some((standard: any) =>
                standard?.measures?.some(isSmePendingMeasure)
              )
          ).length;

          return (
            <div
              className={`review-stage-section${
                isSMEReviewStage || isFinalReviewStage
                  ? ""
                  : " review-stage-section--full"
              }`}
            >
              <div className="review-stage-left">
                {status.length > 0 && (
                  <div className="review-status-strip">
                    <span className="review-status-label">Review Status:</span>
                    <Dropdown
                      disabled={
                        updatingStatus ||
                        !isActiveReview ||
                        (isAdministrator() &&
                          (selectedStatus?.statusCode?.toLowerCase() ===
                            ReviewStatusCode.RsReviewCompleted?.toLowerCase() ||
                            selectedStatus?.statusCode?.toLowerCase() ===
                              ReviewStatusCode.RsResolutionReviewCompleted?.toLowerCase())) ||
                        canDisableStatusRelatedInputs()
                      }
                      value={selectedStatus}
                      onChange={(event: DropdownChangeEvent) =>
                        setReviewStatus(event.value)
                      }
                      options={[...status].sort((a, b) =>
                        a.statusName.localeCompare(b.statusName)
                      )}
                      optionLabel="statusName"
                      placeholder={provider.reviewStatus}
                      className="review-status-dropdown"
                    />

                    {updatingStatus && (
                      <i
                        className="pi pi-spin pi-spinner"
                        style={{ fontSize: "1.2em", marginLeft: "8px" }}
                      />
                    )}

                    {canStartReview &&
                      performanceAreas &&
                      performanceAreas.length > 0 && (
                        <Button
                          className="start-button"
                          label="Start"
                          onClick={() => StartReview()}
                        />
                      )}

                    {isFinalReviewStage && (
                      <div className="tier-determination-group">
                        <span className="review-status-label">
                          Tier Determination:
                        </span>
                        <div className="tier-determination-box">
                          {(isFinalReviewer() || isAdministrator()) &&
                          isCalculate ? (
                            <ProgressSpinner
                              style={{ width: "20px", height: "20px" }}
                              strokeWidth="4"
                            />
                          ) : (
                            calculatedTier ||
                            provider.currentTierIdentifier ||
                            "-"
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {isSMEReviewStage && (
                  <SmeReviewStatsComponent
                    pendingMeasuresCount={smePendingMeasuresCount}
                    performanceAreasRemaining={performanceAreasRemaining}
                  />
                )}

                {isFinalReviewStage && (
                  <div className="review-stage-outcome">
                    <OutcomeStatusTableComponent
                      data={outcomeStatusTable || []}
                      loading={
                        (isFinalReviewer() || isAdministrator()) &&
                        outcomeStatusLoading
                      }
                      error={outcomeStatusError}
                    />
                  </div>
                )}
              </div>

              {(isSMEReviewStage || isFinalReviewStage) && (
                <ReviewStageTrackerComponent
                  reviewers={reviewers}
                  title="Review Stage Tracker"
                  subtitle="Click on each status to view user activity log"
                  fetchActivityLogs={fetchActivityLogs}
                />
              )}
            </div>
          );
        })()}

      <br />

      {provider.attestationFile ? (
        <div className="attestation-button-wrapper">
          <Button
            onClick={() => {
              downloadDataFile(provider.attestationFile, provider?.mpi);
            }}
            icon="pi pi-download"
            className="p-button-secondary"
          >
            &nbsp;{provider.attestationFile}
          </Button>
        </div>
      ) : null}

      <div>{renderTierView()}</div>

      <Dialog
        header="File not found!"
        visible={showConfirmDialog}
        onHide={hideDialog}
        footer={
          <div style={{ display: "flex", justifyContent: "end" }}>
            <Button
              label="Close"
              icon="pi pi-times"
              className="p-button-secondary"
              onClick={hideDialog}
              autoFocus
            />
          </div>
        }
      >
        <div>
          <p>{Constants.FILE_NOT_FOUND_ERROR}</p>
        </div>
      </Dialog>

      {sidebarVisible && (
        <CommentsSidebar
          {...{
            sidebarVisible,
            handleCommentsSidebarHide,
            commentProviderMPI,
            commentMeasureId,
            commentMeasureNumber,
            commentLevel,
            updatePerformanceAreasForComments,
            providerAgencyName: provider?.providerAgencyName,
            externalSave: () => {
              if (commentProviderMPI) {
                void fetchAllCommentsForProvider(commentProviderMPI);
                void fetchAllCommentsForProviderLevel(commentProviderMPI);
              }
            },
            providerType: ProviderType.RS,
          }}
        />
      )}

      <CommonDialogWindowComponent ref={dialogRef} />

      <SendEmailDialogComponent
        visible={emailDialogVisible}
        onHide={handleEmailDialogHide}
        onSend={handleSendEmail}
        providerInfo={{
          agencyName: provider?.providerAgencyName,
          mpi: provider?.mpi,
          primaryContactFirstName:
            providerContactDetails?.primaryContactFirstName,
          primaryContactLastName:
            providerContactDetails?.primaryContactLastName,
          primaryContactEmail: providerContactDetails?.primaryContactEmail,
        }}
      />

      <Toast ref={toast} />
    </div>
  );
};

export default RHProviderProfileComponent;
